// Negotiation Credits System Types

export interface NegotiationCredit {
  id: string;
  user_id: string;
  seller_type_id: string | null;
  credits_total: number;
  credits_used: number;
  source: 'plan_purchase' | 'admin_grant' | 'bonus';
  purchased_at: string;
  expires_at: string;
  is_active: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface SellerCreditsStatus {
  user_id: string;
  display_name: string | null;
  is_verified: boolean;
  verification_expires_at: string | null;
  seller_type_name: string | null;
  seller_type_label: string | null;
  negotiations_per_year: number | null;
  priority_bonus: number | null;
  recharge_discount_percent: number; // Percentual de desconto na recarga (0-100)
  // Novo sistema de boost
  boost_total: number;
  boost_plan_total: number;
  boost_performance_total: number;
  highest_plan_tier: number;
  // Créditos (não expiram mais)
  total_credits: number;
  used_credits: number;
  available_credits: number;
  is_unlimited: boolean;
}

export interface NegotiationReport {
  id: string;
  negotiation_id: string;
  reporter_id: string;
  reported_user_id: string;
  report_category: ReportCategory;
  report_details: string | null;
  evidence_message_ids: string[] | null;
  status: ReportStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  action_taken: string | null;
  created_at: string;
}

export interface NegotiationReportEnriched extends NegotiationReport {
  reporter_name: string | null;
  reporter_email: string | null;
  reporter_avatar: string | null;
  reported_name: string | null;
  reported_email: string | null;
  reported_avatar: string | null;
}

export type ReportCategory = 
  | 'whatsapp_redirect' 
  | 'outside_deal' 
  | 'scam_suspicion' 
  | 'inappropriate' 
  | 'other';

export type ReportStatus = 'pending' | 'reviewed' | 'action_taken' | 'dismissed';

export const REPORT_CATEGORY_LABELS: Record<ReportCategory, string> = {
  whatsapp_redirect: 'Tentou levar para WhatsApp',
  outside_deal: 'Tentou fechar por fora',
  scam_suspicion: 'Suspeita de golpe',
  inappropriate: 'Comportamento inadequado',
  other: 'Outro motivo',
};

export const REPORT_STATUS_LABELS: Record<ReportStatus, string> = {
  pending: 'Pendente',
  reviewed: 'Analisado',
  action_taken: 'Ação tomada',
  dismissed: 'Descartado',
};

export interface UserCancellationStats {
  user_id: string;
  display_name?: string | null;
  avatar_url?: string | null;
  total_cancellations: number;
  cancellations_last_30_days: number;
  cancellations_last_90_days: number;
  last_cancellation_at: string | null;
  flagged_for_review: boolean;
  flag_reason: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  created_at?: string;
  updated_at?: string;
}

export type CancellationCategory = 
  | 'buyer_issue' 
  | 'seller_issue' 
  | 'price_disagreement' 
  | 'changed_mind' 
  | 'no_response' 
  | 'other';

export const CANCELLATION_CATEGORY_LABELS: Record<CancellationCategory, string> = {
  buyer_issue: 'Problema com o comprador',
  seller_issue: 'Problema com o vendedor',
  price_disagreement: 'Desacordo no preço',
  changed_mind: 'Mudei de ideia',
  no_response: 'Sem resposta da outra parte',
  other: 'Outro motivo',
};

// Extended negotiation type with new fields
export interface ExtendedNegotiation {
  id: string;
  listing_id: string;
  buyer_id: string;
  seller_id: string;
  status: 'active' | 'completed' | 'cancelled' | 'expired';
  seller_accepted_terms: boolean;
  buyer_accepted_terms: boolean;
  seller_terms_accepted_at: string | null;
  buyer_terms_accepted_at: string | null;
  last_activity_at: string;
  created_at: string;
  completed_at: string | null;
  cancelled_at: string | null;
  cancelled_by: string | null;
  // New fields for dual confirmation
  buyer_confirmed_complete: boolean;
  seller_confirmed_complete: boolean;
  buyer_confirmed_at: string | null;
  seller_confirmed_at: string | null;
  // Cancellation details
  cancellation_reason: string | null;
  cancellation_category: string | null;
  // Expiration
  expired_at: string | null;
  reopened_at: string | null;
  reopened_by: string | null;
  // Credit tracking
  credit_consumed: boolean;
  credit_id: string | null;
}

// Credit ledger entry for admin view
export interface CreditLedgerEntry {
  id: string;
  user_id: string;
  seller_type_id: string | null;
  credits_total: number;
  credits_used: number;
  source: string;
  expires_at: string;
  is_active: boolean;
  notes: string | null;
  created_at: string;
  // Computed
  credits_remaining: number;
  is_expired: boolean;
  seller_type_label?: string;
}
